/**
 * Strategy Planner Simulation Calculations (SF-64)
 *
 * Client-side province state projection. Formulas MUST match backend systems:
 * - BuildingSystem.computeModifier()
 * - EconomySystem.processTick()
 * - MilitaryDraftSystem
 * - ScienceSystem.calculateBonus()
 */

import type { StrategySettings } from '../types/strategy';
import { RACE_UNIT_STATS } from '../constants/game';

export interface ProjectedState {
  totalLand: number;
  population: number;
  peasants: number;
  army: number;
  soldiers: number;
  offSpecs: number;
  defSpecs: number;
  elites: number;
  thieves: number;
  wizards: number;
  buildingEfficiency: number;
  gold: GoldProjection;
  food: ResourceProjection;
  runes: ResourceProjection;
  offense: MilitaryProjection;
  defense: MilitaryProjection;
  tpa: number;
  wpa: number;
  buildingTotal: number;
}

export interface GoldProjection {
  baseIncome: number;
  bankBonus: number;
  honourMod: number;
  alchemyMod: number;
  totalIncome: number;
  militaryWages: number;
  draftCost: number;
  scienceCost: number;
  totalExpenses: number;
  net: number;
}

export interface ResourceProjection {
  production: number;
  consumption: number;
  net: number;
}

export interface MilitaryProjection {
  raw: number;
  me: number;
  modified: number;
}

// Honour title income modifiers
const HONOUR_INCOME_MOD: Record<string, number> = {
  PEASANT: 1.0, KNIGHT: 1.0, LORD: 1.02, BARON: 1.04,
  VISCOUNT: 1.07, COUNT: 1.10, MARQUIS: 1.13,
  DUKE: 1.16, PRINCE: 1.22,
};

// Wage rates per unit
const WAGE_SOLDIER = 0.5;
const WAGE_SPECIALIST = 1.0;
const WAGE_ELITE = 1.5;
const WAGE_THIEF = 0.75;

// Income rates
const INCOME_EMPLOYED = 2.75;
const INCOME_UNEMPLOYED = 0.75;
const BANK_BONUS_PER = 0.02;

// Science rates: [bpa, gcPerBook]
const SCIENCE_RATES: Record<string, [number, number]> = {
  NO_RESEARCH: [0, 0],
  MINIMAL: [0.3, 6],
  LIMITED: [0.4, 7],
  SUSTAINED: [0.5, 9],
  ACTIVE: [0.7, 13],
  FOCUSED: [0.9, 17],
  ACCELERATED: [1.2, 22],
  INTENSIVE: [1.5, 27],
  RUSHED: [2.0, 36],
  EXTREME: [3.0, 50],
};

// Draft rates: [speed, baseCost]
const DRAFT_RATES: Record<string, [number, number]> = {
  NONE: [0, 0],
  RESERVIST: [0.003, 30],
  NORMAL: [0.006, 50],
  AGGRESSIVE: [0.01, 75],
  EMERGENCY: [0.015, 110],
};

export interface ProvinceSnapshot {
  totalLand: number;
  population: number;
  race: string;
  honourTitle: string;
  alchemyBooks: number;
}

/**
 * Compute base military efficiency from wage rate.
 * ME = (4/3 × w³ - 8 × w² + 22.26 × w + 84.4) / 100
 */
function computeBaseME(wageRate: number): number {
  const w = wageRate / 100.0;
  return Math.round((4 / 3) * w * w * w - 8 * w * w + 22.26 * w + 84.4) / 100;
}

/**
 * Compute building effect percentage.
 */
function buildingPct(settings: StrategySettings, building: string): number {
  return (settings.buildings[building] || 0) / 100;
}

/**
 * Calculate building efficiency (simplified steady-state assumption).
 * BE ≈ 0.5 × (1 + jobPerformance) where jobPerformance ≈ 1.0 at steady state
 * For planner: assume steady-state BE ≈ 1.0
 */
function estimateBE(_settings: StrategySettings, _totalLand: number): number {
  return 1.0;
}

/**
 * Project province state from planner settings.
 */
export function projectState(settings: StrategySettings, snapshot: ProvinceSnapshot): ProjectedState {
  const { totalLand, population, race, honourTitle, alchemyBooks } = snapshot;
  const be = estimateBE(settings, totalLand);

  // Building counts and percentages
  const buildingTotal = Object.values(settings.buildings).reduce((sum, v) => sum + (v || 0), 0);

  const bankCount = Math.floor(totalLand * (settings.buildings.BANK || 0) / 100);
  const farmCount = Math.floor(totalLand * (settings.buildings.FARM || 0) / 100);
  const towerCount = Math.floor(totalLand * (settings.buildings.TOWER || 0) / 100);

  // TG/Fort percentages for ME calculation
  const tgPct = buildingPct(settings, 'TRAINING_GROUND');
  const fortPct = buildingPct(settings, 'FORT');
  const tgBonus = 1.5 * be * tgPct;
  const fortBonus = 1.5 * be * fortPct;

  // Schools/libraries for science
  const schoolPct = buildingPct(settings, 'SCHOOL');
  const libraryPct = buildingPct(settings, 'LIBRARY');
  const schoolsMod = 1 - 1.5 * be * schoolPct * (1 - schoolPct);
  const libraryEffect = 1 + 2.0 * be * libraryPct;

  // Armoury modifiers
  const armouryPct = buildingPct(settings, 'ARMOURY');
  const draftCostMod = 1 - 2.0 * be * armouryPct;

  // Population breakdown
  const wizards = Math.floor(settings.wizardPA * totalLand);
  const nonWizardPop = population - wizards;
  const army = Math.floor(nonWizardPop * (settings.draftLevel / 100));
  const peasants = nonWizardPop - army;

  // Soldiers = ~35% of army (planner assumption)
  const soldiers = Math.floor(army * 0.35);

  // Army composition (per-acre targets)
  const offSpecs = Math.floor(settings.armyComp.offSpec * totalLand);
  const defSpecs = Math.floor(settings.armyComp.defSpec * totalLand);
  const elites = Math.floor(settings.armyComp.elite * totalLand);
  const thieves = Math.floor(settings.thiefPA * totalLand);

  // --- GOLD ---
  const employed = Math.min(peasants, Math.floor(totalLand * 25 * 0.67));
  const unemployed = Math.max(0, peasants - employed);
  const baseIncome = employed * INCOME_EMPLOYED + unemployed * INCOME_UNEMPLOYED;
  const bankBonus = baseIncome * (bankCount * BANK_BONUS_PER);

  const honourMod = HONOUR_INCOME_MOD[honourTitle] || 1.0;
  // Alchemy science bonus (simplified: sqrt-based, assume existing books)
  const alchemyMod = alchemyBooks > 0 ? 1.0 + Math.min(0.4, Math.sqrt(alchemyBooks / totalLand) * 1.4 / 100 * libraryEffect) : 1.0;
  const totalIncome = (baseIncome + bankBonus) * honourMod * alchemyMod;

  // Wages (wizards do NOT consume wages)
  const militaryWages = soldiers * WAGE_SOLDIER + (offSpecs + defSpecs) * WAGE_SPECIALIST
    + elites * WAGE_ELITE + thieves * WAGE_THIEF;

  // Draft cost
  const draftInfo = DRAFT_RATES[settings.draftRate] || [0, 0];
  const draftedPerTick = Math.floor(peasants * draftInfo[0]);
  const draftCost = draftedPerTick * draftInfo[1] * Math.max(0, draftCostMod);

  // Science cost
  const scienceInfo = SCIENCE_RATES[settings.scienceRate] || [0, 0];
  const booksPerTick = Math.floor(totalLand * scienceInfo[0]);
  const scienceCost = booksPerTick * scienceInfo[1] * Math.max(0, schoolsMod);

  const totalExpenses = militaryWages + draftCost + scienceCost;
  const netGold = totalIncome - totalExpenses;

  // --- FOOD ---
  const foodProduction = farmCount * 70 * be;
  const foodConsumption = population;

  // --- RUNES ---
  const runeProduction = towerCount * 12 * be;
  const runeConsumption = 0; // Simplified: no spell costs in steady state

  // --- MILITARY ---
  const raceStats = RACE_UNIT_STATS[race?.toUpperCase()] || RACE_UNIT_STATS.HUMAN;
  const baseME = computeBaseME(settings.wageRate);

  const rawOffense = soldiers * raceStats.soldier.off
    + offSpecs * raceStats.offense.off
    + elites * raceStats.elite.off;
  const ome = Math.round((baseME + tgBonus) * honourMod * 100) / 100;
  const modifiedOffense = Math.floor(rawOffense * ome);

  const rawDefense = soldiers * raceStats.soldier.def
    + defSpecs * raceStats.defense.def
    + elites * raceStats.elite.def;
  const dme = Math.round((baseME + fortBonus) * 100) / 100;
  const modifiedDefense = Math.floor(rawDefense * dme);

  // TPA / WPA
  const tpa = totalLand > 0 ? thieves / totalLand : 0;
  const wpa = totalLand > 0 ? wizards / totalLand : 0;

  return {
    totalLand,
    population,
    peasants,
    army,
    soldiers,
    offSpecs,
    defSpecs,
    elites,
    thieves,
    wizards,
    buildingEfficiency: be,
    gold: {
      baseIncome,
      bankBonus,
      honourMod,
      alchemyMod,
      totalIncome,
      militaryWages,
      draftCost,
      scienceCost,
      totalExpenses,
      net: netGold,
    },
    food: { production: foodProduction, consumption: foodConsumption, net: foodProduction - foodConsumption },
    runes: { production: runeProduction, consumption: runeConsumption, net: runeProduction - runeConsumption },
    offense: { raw: rawOffense, me: ome, modified: modifiedOffense },
    defense: { raw: rawDefense, me: dme, modified: modifiedDefense },
    tpa,
    wpa,
    buildingTotal,
  };
}

/**
 * Compute science growth curve for sparkline (projected bonus over N days).
 */
export function computeScienceGrowthCurve(
  totalLand: number,
  scienceRate: string,
  allocationPct: number,
  existingBooks: number,
  libraryPct: number,
  days: number = 90,
): number[] {
  const scienceInfo = SCIENCE_RATES[scienceRate] || [0, 0];
  const dailyBPA = scienceInfo[0];
  const booksPerDay = totalLand * dailyBPA * (allocationPct / 100);
  const be = 1.0; // Steady state
  const libEffect = 1 + 2.0 * be * libraryPct;

  const points: number[] = [];
  for (let d = 0; d <= days; d += 5) {
    const totalBooks = existingBooks + booksPerDay * d;
    const bpa = totalLand > 0 ? totalBooks / totalLand : 0;
    const bonus = Math.sqrt(bpa) * libEffect * 1.4 / 100; // Simplified: no race/personality mods
    points.push(bonus);
  }
  return points;
}
